/**
 * Toasts via shadcn/ui Sonner: bottom-left, polite live region. Errors are
 * also mirrored into an assertive live region (sonner only has a polite one).
 */
import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { common } from "../../copy/common";
import { Toaster as ShadToaster } from "./shadcn/sonner";

const UNDO_MS = 8000;

let assertiveText = "";
const assertiveListeners = new Set<() => void>();

function announceAssertive(text: string): void {
  // Clear first so the same message is announced again.
  assertiveText = "";
  for (const l of assertiveListeners) l();
  setTimeout(() => {
    assertiveText = text;
    for (const l of assertiveListeners) l();
  }, 50);
}

export const notify = {
  success(message: string, opts: { action?: { label: string; onClick: () => void } } = {}): void {
    toast(message, opts.action ? { action: opts.action } : {});
  },
  info(message: string): void {
    toast(message);
  },
  error(message: string): void {
    toast.error(message);
    announceAssertive(message);
  },
  /** Destructive action done; offer Undo for 8 s. */
  undo(message: string, onUndo: () => void): void {
    toast(message, { duration: UNDO_MS, action: { label: common.undo, onClick: onUndo } });
  },
  dismiss(id: string): void {
    toast.dismiss(id);
  },
};

function AssertiveRegion() {
  const text = useSyncExternalStore(
    (l) => {
      assertiveListeners.add(l);
      return () => assertiveListeners.delete(l);
    },
    () => assertiveText,
    () => "",
  );
  return (
    <div aria-live="assertive" role="alert" className="sr-only">
      {text}
    </div>
  );
}

export function Toaster() {
  return (
    <>
      <ShadToaster
        position="bottom-left"
        closeButton
        toastOptions={{
          classNames: {
            toast: "!rounded-xl !shadow-dialog !font-sans !text-sm",
            // Solid accent action with AA contrast in both themes.
            actionButton: "!bg-primary !text-primary-foreground !font-medium !rounded-md !min-h-7 !px-3",
            closeButton: "!bg-popover !border-border !text-foreground",
          },
        }}
      />
      <AssertiveRegion />
    </>
  );
}
